import { Markdown } from "@/components/ui/markdown";
import { type ProjectWikiRef, ProjectWikisAside } from "@/components/wiki/project-wikis-aside";
import { WikiSidebar } from "@/components/wiki/wiki-sidebar";
import { ApiError, api } from "@/lib/api";
import { preprocessWikiMarkdown } from "@/lib/wiki-markdown";
import { createFileRoute, notFound } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

/**
 * `/wiki/*` — a single company wiki page, rendered inside Hub. The
 * splat lets nested wiki slugs (`workflow/automation`) match too:
 * subfolders of `wiki/`, linked with literal slashes. A missing page
 * surfaces as a 404.
 *
 * The page wears the same chrome as the `/wiki` home — the "Netlion
 * Wiki" header and the right-hand "Project wikis" column — so a
 * reader who deep-links into a page keeps the full navigation.
 */

type WikiPageSummary = { slug: string; title: string };

type WikiPage = { slug: string; title: string; content: string };

type WikiPageResponse = {
  page: WikiPage;
  pages: WikiPageSummary[];
  sidebarMarkdown: string | null;
};

type ProjectsResponse = { projects: ProjectWikiRef[] };

export const Route = createFileRoute("/_app/wiki/$")({
  loader: async ({ params }) => {
    // `_splat` is the matched tail of the URL: `workflow/automation`
    // for `/wiki/workflow/automation`, `tools` for `/wiki/tools`. The
    // wiki API takes the slug as a path tail too — encode each
    // segment so a stray space or `?` inside a wiki name doesn't
    // corrupt the request, but keep the `/` folder separators.
    const slug = (params._splat ?? "")
      .split("/")
      .map((seg) => encodeURIComponent(seg))
      .join("/");
    try {
      const [wiki, projectsRes] = await Promise.all([
        api.get<WikiPageResponse>(`/api/wiki/${slug}`),
        api.get<ProjectsResponse>("/api/projects?pageSize=200"),
      ]);
      return { wiki, projects: projectsRes.projects };
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) throw notFound();
      throw err;
    }
  },
  component: CompanyWikiPageDetail,
});

function CompanyWikiPageDetail() {
  const { t } = useTranslation();
  const { wiki, projects } = Route.useLoaderData();
  const { page, pages, sidebarMarkdown } = wiki;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-heading text-2xl font-bold">{t("wk.companyTitle")}</h1>
        <p className="text-muted-foreground text-sm mt-1">{t("wk.companyIntro")}</p>
      </div>

      <div className="flex flex-col gap-6 lg:flex-row">
        <WikiSidebar pages={pages} sidebarMarkdown={sidebarMarkdown} currentSlug={page.slug} />

        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-xl font-bold mb-4">{page.title}</h2>

          <div className="rounded-md border bg-card p-6">
            <Markdown content={preprocessWikiMarkdown(page.content, "/wiki")} />
          </div>
        </div>

        <ProjectWikisAside projects={projects} />
      </div>
    </div>
  );
}
